import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal, computed } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { PublicBookingService } from '../../../services/bookings/public-booking.service';
import { getReadableInk } from '../microsite-colors';

interface BookingConfirmationData {
  eventId: string;
  businessId: string;
  businessName: string;
  businessAddress?: string | null;
  clientName: string;
  serviceName: string;
  professionalName?: string | null;
  startDate: string;
  endDate: string;
  primaryColor?: string | null;
  secondaryColor?: string | null;
  logoUrl?: string | null;
  whatsApp?: string | null;
}

@Component({
  selector: 'app-booking-confirmation',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './booking-confirmation.html',
  styleUrls: ['../microsite-tokens.css', './booking-confirmation.css'],
  host: {
    '[style.--ms-brand]': 'brandColor()',
    '[style.--ms-brand-ink]': 'brandInk()',
  },
})
export class BookingConfirmation implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private bookingService = inject(PublicBookingService);

  bookingData = signal<BookingConfirmationData | null>(null);
  isLoading = signal(true);
  slug = signal<string | null>(null);

  brandColor = computed(() => this.bookingData()?.primaryColor || '#2563eb');
  brandInk = computed(() => getReadableInk(this.brandColor()));

  // "Lunes, 26 de octubre"
  dayLabel = computed(() => {
    const data = this.bookingData();
    if (!data) return '';
    const label = new Date(data.startDate).toLocaleDateString('es-CO', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });
    return label.charAt(0).toUpperCase() + label.slice(1);
  });

  timeRange = computed(() => {
    const data = this.bookingData();
    if (!data) return '';
    return `${this.formatTime(data.startDate)} – ${this.formatTime(data.endDate)}`;
  });

  firstName = computed(() => (this.bookingData()?.clientName || '').trim().split(/\s+/)[0] || '');

  // Short reference the client can quote to the business
  referenceCode = computed(() => {
    const id = this.bookingData()?.eventId || '';
    return id.replace(/-/g, '').slice(0, 6).toUpperCase();
  });

  whatsAppUrl = computed(() => {
    const data = this.bookingData();
    if (!data?.whatsApp) return null;
    const phone = data.whatsApp.replace(/[\s\-\(\)]+/g, '');
    const message = encodeURIComponent(
      `Hola, tengo una cita de ${data.serviceName} el ${this.dayLabel().toLowerCase()} a las ${this.formatTime(data.startDate)} (reserva ${this.referenceCode()}). Quiero cambiarla o cancelarla.`
    );
    return `https://wa.me/${phone}?text=${message}`;
  });

  getGoogleMapsUrl = computed(() => {
    const address = this.bookingData()?.businessAddress;
    if (!address) return '#';
    const encodedAddress = encodeURIComponent(address);
    return `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
  });

  ngOnInit(): void {
    // Get data from query params or state
    this.route.queryParams.subscribe((params) => {
      const eventId = params['eventId'];
      const slug = params['slug'];

      if (slug) {
        this.slug.set(slug);
      }

      if (eventId && slug) {
        this.loadBookingData(slug, eventId);
      } else {
        // If no data, redirect to home or show error
        this.isLoading.set(false);
      }
    });
  }

  private loadBookingData(slug: string, eventId: string): void {
    // Load business info
    this.bookingService.getBusinessBySlug(slug).subscribe({
      next: (business) => {
        // For now, we'll use the data from query params
        // In a real scenario, you'd fetch the event details from the API
        const params = this.route.snapshot.queryParams;
        this.bookingData.set({
          eventId: eventId,
          businessId: business.businessId,
          businessName: business.name,
          businessAddress: business.address,
          clientName: params['clientName'] || 'Cliente',
          serviceName: params['serviceName'] || 'Servicio',
          professionalName: params['professionalName'] || null,
          startDate: params['startDate'] || new Date().toISOString(),
          endDate: params['endDate'] || new Date().toISOString(),
          primaryColor: business.primaryColor,
          secondaryColor: business.secondaryColor,
          logoUrl: business.logoUrl,
          whatsApp: business.whatsApp,
        });
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
      },
    });
  }

  private formatTime(dateStr: string): string {
    const date = new Date(dateStr);
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const period = hours >= 12 ? 'p.m.' : 'a.m.';
    const hour12 = hours % 12 === 0 ? 12 : hours % 12;
    const minuteStr = String(minutes).padStart(2, '0');
    return `${hour12}:${minuteStr} ${period}`;
  }

  goToHome(): void {
    const slug = this.slug();
    if (slug) {
      this.router.navigate(['/', slug]);
    } else {
      this.router.navigate(['/']);
    }
  }

  getGoogleCalendarUrl(): string {
    const data = this.bookingData();
    if (!data) return '#';
    const title = encodeURIComponent(`${data.serviceName} - ${data.businessName}`);
    const start = this.toGoogleCalendarDate(data.startDate);
    const end = this.toGoogleCalendarDate(data.endDate);
    const location = encodeURIComponent(data.businessAddress || '');
    const details = encodeURIComponent(`Reservado por: ${data.clientName}${data.professionalName ? '\nProfesional: ' + data.professionalName : ''}`);
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${start}/${end}&location=${location}&details=${details}`;
  }

  private toGoogleCalendarDate(dateStr: string): string {
    const d = new Date(dateStr);
    return d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  }
}
